import type { UserCreateForm } from "src/types";
import apiRequest from "src/utils/api-request";
import withApiPath from "src/utils/with-api-path";

export interface Admin {
	uuid: string;
	email: string;
	name: string;
	profileImageUrl: string;
}

export interface TwoFactorSetupResponse {
	otpUrl: string;
	secret: string;
	backupCodes: string[];
}

export interface TwoFactorStatusResponse {
	enabled: boolean;
	verifiedAt?: string;
}

const authProvider = {
	async getCookie(): Promise<string> {
		const response = await apiRequest.get(withApiPath(`/auth/csrf-cookie`));
		const csrf = response.headers["x-csrf-token"];

		apiRequest.defaults.headers.common["x-csrf-token"] = csrf;
		return csrf ?? "";
	},

	async login(user: UserCreateForm): Promise<{ requires2FA?: boolean }> {
		const response = await apiRequest.post(withApiPath(`/auth/login`), user);
		return response.data || {};
	},

	async getMe(): Promise<Admin> {
		const response = await apiRequest.get(withApiPath(`/auth/me`));
		return response.data;
	},

	async logout(): Promise<void> {
		await apiRequest.post(withApiPath(`/auth/logout`));
	},

	// 2FA Methods
	async get2FAStatus(): Promise<TwoFactorStatusResponse> {
		const response = await apiRequest.get(withApiPath(`/auth/2fa/status`));
		return response.data;
	},

	async setup2FA(): Promise<TwoFactorSetupResponse> {
		const response = await apiRequest.post(withApiPath(`/auth/2fa/setup`));
		return response.data;
	},

	async enable2FA(
		code: string,
		secret: string,
		backupCodes: string[],
	): Promise<void> {
		await apiRequest.post(withApiPath(`/auth/2fa/enable`), {
			code,
			secret,
			backupCodes,
		});
	},

	async disable2FA(code: string): Promise<void> {
		await apiRequest.post(withApiPath(`/auth/2fa/disable`), { code });
	},

	async verify2FA(code: string): Promise<void> {
		await apiRequest.post(withApiPath(`/auth/2fa/verify`), { code });
	},

	async regenerateBackupCodes(
		code: string,
	): Promise<{ backupCodes: string[] }> {
		const response = await apiRequest.post(
			withApiPath(`/auth/2fa/backup-codes`),
			{ code },
		);
		return response.data;
	},

	// Password management
	async changePassword(
		currentPassword: string,
		newPassword: string,
	): Promise<void> {
		await apiRequest.post(withApiPath(`/auth/password/change`), {
			currentPassword,
			newPassword,
		});
	},

	async initiatePasswordReset(email: string): Promise<void> {
		await apiRequest.post(withApiPath(`/auth/password/reset/initiate`), {
			email,
		});
	},

	async validatePasswordResetToken(token: string): Promise<{ email: string }> {
		const response = await apiRequest.post(
			withApiPath(`/auth/password/reset/validate`),
			{ token },
		);
		return response.data;
	},

	async completePasswordReset(
		token: string,
		newPassword: string,
	): Promise<void> {
		await apiRequest.post(withApiPath(`/auth/password/reset/complete`), {
			token,
			newPassword,
		});
	},
};

export default authProvider;
